import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FibreService } from 'src/app/application/services/fibre.service';
import { FibreOffering } from 'src/app/models/fibre-offering';

interface PivotRow {
  isp: string;
  prices: { [speed: number]: number | null };
}

interface SelectedOffering {
  isp: string;
  speed: number;
  price: number;
}

@Component({
  selector: 'app-fibre',
  templateUrl: './fibre.component.html',
  styleUrls: ['./fibre.component.scss']
})
export class FibreComponent implements OnChanges {

  @Input() application: any;

  @ViewChild('customerFormRef') customerFormRef: ElementRef;

  speedTiers: number[] = [];
  rows: PivotRow[] = [];
  loading = false;
  errorMessage: string | null = null;
  selectedOffering: SelectedOffering | null = null;
  applicationForm: FormGroup;
  showPopia = false;
  submitted = false;
  submitting = false;
  submitError: string | null = null;
  submittedPayload: any = null;

  // CR 2026-06-12: lets the buyer stop being auto-redirected to this tab when
  // they click their unit. The property list reads the same key.
  autoRedirectOptOut = false;

  private hasLoaded = false;
  private readonly storageKeyPrefix = 'fibre_submitted_';
  private readonly optOutKeyPrefix = 'fibre_optout_';

  constructor(private _fibre_service: FibreService, private _fb: FormBuilder) {
    this.applicationForm = this._fb.group({
      customerName: ['', Validators.required],
      developmentUnit: [{ value: '', disabled: true }],
      email: ['', [Validators.required, Validators.email]],
      mobile: ['', Validators.required],
      consent: [false, Validators.requiredTrue]
    });
  }

  ngOnChanges(_changes: SimpleChanges): void {
    const developmentId = this.application?.development?.id;

    if (!this.hasLoaded && developmentId) {
      this.hasLoaded = true;
      this.loadPricing(developmentId);
    }

    this.prefillForm();
    this.checkPriorSubmission();
    this.loadOptOut();
  }

  private loadOptOut(): void {
    const token = this.application?.token;
    if (!token) {
      return;
    }
    this.autoRedirectOptOut = !!localStorage.getItem(this.optOutKeyPrefix + token);
  }

  onOptOutChange(checked: boolean): void {
    this.autoRedirectOptOut = checked;
    if (checked) {
      // Collapse everything below the opt-out, including a part-filled form.
      this.selectedOffering = null;
    }
    const token = this.application?.token;
    if (!token) {
      return;
    }
    if (checked) {
      localStorage.setItem(this.optOutKeyPrefix + token, '1');
    } else {
      localStorage.removeItem(this.optOutKeyPrefix + token);
    }
  }

  private checkPriorSubmission(): void {
    const token = this.application?.token;
    if (!token || this.submitted) {
      return;
    }

    const raw = localStorage.getItem(this.storageKeyPrefix + token);
    if (!raw) {
      return;
    }

    try {
      this.submittedPayload = JSON.parse(raw);
      this.submitted = true;
    } catch {
      localStorage.removeItem(this.storageKeyPrefix + token);
    }
  }

  private prefillForm(): void {
    const applicant = this.application?.body?.applicants?.[0] || {};
    const personal = applicant.personal_details || {};
    const contact = applicant.contact_details || {};
    const firstName = this.application?.buyer_first_name || personal.first_name || '';
    const lastName = this.application?.buyer_last_name || personal.last_name || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    const email = this.application?.buyer_email || contact.email_address || '';
    const mobile = this.application?.buyer_mobile || contact.mobile_telephone || '';
    const developmentName = this.application?.development?.name || '';
    const unitNumber = this.application?.reserved_unit?.number || '';
    const developmentUnit = developmentName && unitNumber
      ? `${developmentName} – Unit ${unitNumber}`
      : developmentName;

    this.applicationForm.patchValue({
      customerName: this.applicationForm.get('customerName').value || fullName,
      developmentUnit,
      email: this.applicationForm.get('email').value || email,
      mobile: this.applicationForm.get('mobile').value || mobile
    });
  }

  togglePopia(): void {
    this.showPopia = !this.showPopia;
  }

  onSubmit(): void {
    if (!this.applicationForm.valid || !this.selectedOffering || this.submitting) {
      return;
    }

    const token = this.application?.token;
    if (!token) {
      this.submitError = 'Cannot submit: application reference is missing.';
      return;
    }

    const payload = {
      ...this.applicationForm.getRawValue(),
      offering: this.selectedOffering,
      submitted_at: new Date().toISOString()
    };

    this.submitting = true;
    this.submitError = null;

    this._fibre_service.submit$(token, payload).subscribe(
      () => {
        this.submittedPayload = payload;
        localStorage.setItem(
          this.storageKeyPrefix + token,
          JSON.stringify(payload)
        );
        this.submitted = true;
        this.submitting = false;
      },
      err => {
        this.submitError = err?.error?.error
          || 'Unable to submit your fibre application. Please try again.';
        this.submitting = false;
      }
    );
  }

  private loadPricing(developmentId: number): void {
    this.loading = true;
    this.errorMessage = null;

    this._fibre_service.getPricing$(developmentId).subscribe(
      response => {
        this.buildPivot(response.offerings || []);
        this.loading = false;
      },
      () => {
        this.errorMessage = 'Unable to load fibre pricing. Please try again later.';
        this.loading = false;
      }
    );
  }

  private buildPivot(offerings: FibreOffering[]): void {
    this.speedTiers = [...new Set(offerings.map(o => o.speed))].sort((a, b) => a - b);

    const ispOrder: string[] = [];
    const byIsp: { [isp: string]: { [speed: number]: number } } = {};

    for (const o of offerings) {
      if (!byIsp[o.isp]) {
        byIsp[o.isp] = {};
        ispOrder.push(o.isp);
      }
      byIsp[o.isp][o.speed] = o.price;
    }

    this.rows = ispOrder.map(isp => ({
      isp,
      prices: this.speedTiers.reduce((acc, speed) => {
        acc[speed] = byIsp[isp][speed] ?? null;
        return acc;
      }, {} as { [speed: number]: number | null })
    }));
  }

  selectOffering(isp: string, speed: number, price: number | null): void {
    if (price === null) {
      return;
    }

    this.selectedOffering = { isp, speed, price };

    setTimeout(() => {
      this.customerFormRef?.nativeElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 0);
  }

  isSelected(isp: string, speed: number): boolean {
    return !!this.selectedOffering
      && this.selectedOffering.isp === isp
      && this.selectedOffering.speed === speed;
  }

}
